import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { Product } from '../../types';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useTranslation } from '../../lib/i18n';
interface ProductCardProps {
  product: Product;
  layout?: 'vertical' | 'horizontal';
  onRequestQuote?: (productId: string) => void;
}
export function ProductCard({
  product,
  layout = 'vertical',
  onRequestQuote
}: ProductCardProps) {
  const {
    t,
    lang
  } = useTranslation();
  const soldOut = product.soldOut === true;
  const comingSoon = product.comingSoon === true;
  const disabled = soldOut || comingSoon;
  if (layout === 'horizontal') {
    return <Card hover className={`flex flex-col md:flex-row gap-4 md:gap-6 ${disabled ? 'opacity-70' : ''}`} data-id="element-395">
        <div className="w-full md:w-1/3 flex-shrink-0 relative bg-dark-bg rounded-industrial min-h-[300px] md:min-h-[350px] overflow-hidden" data-id="element-396">
          <img src={product.image} alt={`${product.name} ${product.model}`} className={`w-full h-full object-contain ${disabled ? 'brightness-40 grayscale' : ''}`} data-id="element-397" />
          {disabled && <div className="absolute inset-0 flex items-center justify-center" data-id="element-398">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-399" />
              <div className={`relative ${soldOut ? 'bg-red-600 text-white border-red-400' : 'bg-gradient-to-r from-brand-yellow via-yellow-200 to-brand-yellow text-dark-bg animate-shine border-yellow-300'} font-black text-base tracking-widest uppercase px-6 py-2 rounded-full shadow-[0_0_30px_rgba(253,185,19,0.5)] whitespace-nowrap border-2`} data-id="element-400">
                {soldOut ? lang === 'en' ? 'Sold out' : lang === 'ru' ? 'Продано' : 'Prodato' : lang === 'en' ? 'Coming soon' : lang === 'ru' ? 'Скоро' : 'Uskoro dostupno'}
              </div>
            </div>}
        </div>
        <div className="flex-1 flex flex-col" data-id="element-401">
          <div className="mb-3 md:mb-4" data-id="element-402">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-1" data-id="element-403">
              {product.name}
            </h3>
            <p className="text-base md:text-lg text-brand-yellow font-semibold" data-id="element-404">
              {product.model}
            </p>
          </div>
          <p className="text-sm md:text-base text-gray-300 mb-3 md:mb-4" data-id="element-405">
            {lang === 'en' && product.descriptionEn ? product.descriptionEn : lang === 'ru' && product.descriptionRu ? product.descriptionRu : product.description}
          </p>
          <div className="mb-3 md:mb-4 space-y-2" data-id="element-406">
            <div className="flex items-start gap-2 p-2 bg-brand-yellow bg-opacity-10 rounded border border-brand-yellow border-opacity-30" data-id="element-407">
              <ShieldCheckIcon className="w-4 h-4 md:w-5 md:h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-408" />
              <div data-id="element-409">
                <p className="text-xs md:text-sm font-semibold text-brand-yellow" data-id="element-410">
                  {t.showcase.safetyTitle}
                </p>
                <p className="text-xs text-gray-300" data-id="element-411">{t.showcase.safetyText}</p>
              </div>
            </div>
            {(lang === 'en' && product.highlightsEn ? product.highlightsEn : lang === 'ru' && product.highlightsRu ? product.highlightsRu : product.highlights).slice(0, 3).map((highlight, index) => <div key={index} className="flex items-center gap-2" data-id="element-412">
                  <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-brand-yellow flex-shrink-0" data-id="element-413" />
                  <span className="text-xs md:text-sm text-gray-300" data-id="element-414">
                    {highlight}
                  </span>
                </div>)}
          </div>
          <div className="grid grid-cols-2 gap-2 md:gap-3 mb-3 md:mb-4 p-2 md:p-3 bg-dark-bg rounded-industrial" data-id="element-415">
            <div data-id="element-416">
              <p className="text-xs text-gray-500" data-id="element-417">{t.showcase.capacity}</p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-418">
                {product.specs.capacity}
              </p>
            </div>
            <div data-id="element-419">
              <p className="text-xs text-gray-500" data-id="element-420">{t.showcase.motor}</p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-421">
                {product.specs.motor}
              </p>
            </div>
            <div data-id="element-422">
              <p className="text-xs text-gray-500" data-id="element-423">{t.showcase.battery}</p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-424">
                {lang === 'en' && product.specs.batteryEn ? product.specs.batteryEn : lang === 'ru' && product.specs.batteryRu ? product.specs.batteryRu : product.specs.battery}
              </p>
            </div>
            <div data-id="element-425">
              <p className="text-xs text-gray-500" data-id="element-426">
                {lang === 'en' ? 'Weight' : lang === 'ru' ? 'Вес' : 'Težina'}
              </p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-427">
                {product.specs.weight}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3 md:mb-4 italic" data-id="element-428">
            {t.showcase.batteryNote}
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4 mt-auto" data-id="element-429">
            <div data-id="element-430">
              <div className="flex items-baseline gap-2" data-id="element-431">
                <span className={`text-2xl md:text-3xl font-bold ${disabled ? 'text-gray-500' : 'text-brand-yellow'}`} data-id="element-432">
                  €{product.price.toLocaleString()}
                </span>
                <span className="text-xs text-gray-400" data-id="element-433">
                  {lang === 'en' ? 'excl. VAT' : lang === 'ru' ? 'без НДС' : 'bez PDV'}
                </span>
              </div>
              {!disabled && <p className="text-xs text-gray-500 mt-0.5" data-id="element-434">
                  {lang === 'en' ? 'incl. VAT' : lang === 'ru' ? 'с НДС' : 'Sa PDV'}
                  : €{Math.round(product.price * 1.2).toLocaleString()} ·
                  {lang === 'en' ? ' Availability on request' : lang === 'ru' ? ' Наличие по запросу' : ' Dostupnost na upit'}
                </p>}
            </div>
            {onRequestQuote && <Button variant="primary" size="md" onClick={disabled ? undefined : () => onRequestQuote(product.id)} disabled={disabled} className={`w-full sm:w-auto ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`} data-id="element-435">
                {t.showcase.requestQuote}
                {!disabled && <ArrowRightIcon className="w-4 h-4 ml-2 inline" data-id="element-436" />}
              </Button>}
          </div>
        </div>
      </Card>;
  }
  // Vertical layout
  return <Card hover className={`flex flex-col h-full ${disabled ? 'opacity-70' : ''}`} data-id="element-437">
      <div className="relative w-full h-64 sm:h-80 bg-dark-bg rounded-industrial mb-4 overflow-hidden" data-id="element-438">
        <img src={product.image} alt={`${product.name} ${product.model}`} className={`w-full h-full object-contain ${disabled ? 'brightness-40 grayscale' : ''}`} data-id="element-439" />
        {disabled && <div className="absolute inset-0 flex items-center justify-center" data-id="element-440">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-441" />
            <div className={`relative ${soldOut ? 'bg-red-600 text-white border-red-400' : 'bg-gradient-to-r from-brand-yellow via-yellow-200 to-brand-yellow text-dark-bg animate-shine border-yellow-300'} font-black text-lg tracking-widest uppercase px-8 py-2.5 rounded-full shadow-[0_0_30px_rgba(253,185,19,0.5)] whitespace-nowrap border-2`} data-id="element-442">
              {soldOut ? lang === 'en' ? 'Sold out' : lang === 'ru' ? 'Продано' : 'Prodato' : lang === 'en' ? 'Coming soon' : lang === 'ru' ? 'Скоро' : 'Uskoro dostupno'}
            </div>
          </div>}
      </div>
      <div className="mb-4" data-id="element-443">
        <h3 className="text-xl font-bold text-white mb-1" data-id="element-444">{product.name}</h3>
        <p className="text-base text-brand-yellow font-semibold" data-id="element-445">
          {product.model}
        </p>
      </div>
      <p className="text-gray-300 mb-4 flex-1" data-id="element-446">
        {lang === 'en' && product.descriptionEn ? product.descriptionEn : lang === 'ru' && product.descriptionRu ? product.descriptionRu : product.description}
      </p>
      <div className="mb-4" data-id="element-447">
        <div className="flex items-baseline gap-2" data-id="element-448">
          <span className={`text-2xl font-bold ${disabled ? 'text-gray-500' : 'text-brand-yellow'}`} data-id="element-449">
            €{product.price.toLocaleString()}
          </span>
          <span className="text-xs text-gray-400" data-id="element-450">
            {lang === 'en' ? 'excl. VAT' : lang === 'ru' ? 'без НДС' : 'bez PDV'}
          </span>
        </div>
        {!disabled && <p className="text-xs text-gray-500 mt-0.5" data-id="element-451">
            {lang === 'en' ? 'incl. VAT' : lang === 'ru' ? 'с НДС' : 'Sa PDV'}:
            €{Math.round(product.price * 1.2).toLocaleString()} ·
            {lang === 'en' ? ' Availability on request' : lang === 'ru' ? ' Наличие по запросу' : ' Dostupnost na upit'}
          </p>}
      </div>
      {onRequestQuote && <Button variant="primary" size="md" className={`w-full ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`} onClick={disabled ? undefined : () => onRequestQuote(product.id)} disabled={disabled} data-id="element-452">
          {t.showcase.requestQuote}
          {!disabled && <ArrowRightIcon className="w-4 h-4 ml-2 inline" data-id="element-453" />}
        </Button>}
    </Card>;
}